import React, { useMemo, useRef, useState } from "react";
import Image from "@tiptap/extension-image";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const alignToMargins = (align) => {
  switch (align) {
    case "left":
      return { marginLeft: "0", marginRight: "auto" };
    case "right":
      return { marginLeft: "auto", marginRight: "0" };
    case "center":
    default:
      return { marginLeft: "auto", marginRight: "auto" };
  }
};

const toStyleString = (width, align) => {
  const w = clamp(Number(width || 100), 10, 100);
  const margins = alignToMargins(align);

  // Persisted inline style ensures it works in BOTH editor and viewer
  return [
    "display:block",
    `width:${w}%`,
    `margin-left:${margins.marginLeft}`,
    `margin-right:${margins.marginRight}`,
    "max-width:100%",
    "height:auto",
  ].join(";");
};

function ResizableImageView(props) {
  const { node, updateAttributes, deleteNode, selected } = props;
  const wrapperRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const widthPct = useMemo(
    () => clamp(Number(node.attrs.width || 100), 10, 100),
    [node.attrs.width]
  );

  const align = node.attrs.align || "center";

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const parent = wrapper.parentElement;
    if (!parent) return;

    const parentRect = parent.getBoundingClientRect();
    const startX = e.clientX;
    const startWidth = widthPct;

    setIsDragging(true);

    const onMove = (ev) => {
      const deltaPx = ev.clientX - startX;
      const deltaPct = (deltaPx / parentRect.width) * 100;
      const next = clamp(startWidth + deltaPct, 10, 100);
      updateAttributes({ width: Math.round(next) });
    };

    const onUp = () => {
      setIsDragging(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <NodeViewWrapper
      ref={wrapperRef}
      className={[
        "tiptap-image-wrapper",
        selected ? "is-selected" : "",
        isDragging ? "is-dragging" : "",
      ].join(" ")}
      contentEditable={false}
    >
      {selected && (
        <div className="tiptap-image-controls" contentEditable={false}>
          <div className="tiptap-image-controls__group">
            <button type="button" onClick={() => updateAttributes({ width: 10 })}>10%</button>
            <button type="button" onClick={() => updateAttributes({ width: 25 })}>25%</button>
            <button type="button" onClick={() => updateAttributes({ width: 50 })}>50%</button>
            <button type="button" onClick={() => updateAttributes({ width: 75 })}>75%</button>
            <button type="button" onClick={() => updateAttributes({ width: 100 })}>100%</button>
          </div>

          <div className="tiptap-image-controls__group">
            <button type="button" onClick={() => updateAttributes({ align: "left" })}>Left</button>
            <button type="button" onClick={() => updateAttributes({ align: "center" })}>Center</button>
            <button type="button" onClick={() => updateAttributes({ align: "right" })}>Right</button>
          </div>

          <div className="tiptap-image-controls__group">
            <button type="button" className="danger" onClick={() => deleteNode()}>
              Remove
            </button>
          </div>
        </div>
      )}

      <div className="tiptap-image-frame" contentEditable={false}>
        <img
          src={node.attrs.src}
          alt={node.attrs.alt || ""}
          title={node.attrs.title || ""}
          style={{
            // IMPORTANT: reflect the same logic as persisted HTML style
            width: `${widthPct}%`,
            display: "block",
            maxWidth: "100%",
            height: "auto",
            ...alignToMargins(align),
            borderRadius: "6px",
          }}
        />

        {selected && (
          <>
            <span className="tiptap-image-handle left" onMouseDown={startResize} />
            <span className="tiptap-image-handle right" onMouseDown={startResize} />
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
}

const ResizableImage = Image.extend({
  name: "image",

  // ✅ Make it block-level so width/alignment behave consistently
  inline() {
    return false;
  },

  group() {
    return "block";
  },

  addAttributes() {
    return {
      ...this.parent?.(),

      width: {
        default: 100,
        parseHTML: (element) => {
          // prefer data-width
          const dw = element.getAttribute("data-width");
          if (dw) return Number(dw);

          // fallback: parse from style="width:..%"
          const w = element.style?.width || "";
          const match = w.match(/^(\d+)%$/);
          return match ? Number(match[1]) : 100;
        },
        renderHTML: (attributes) => {
          const w = clamp(Number(attributes.width || 100), 10, 100);
          return { "data-width": String(w) };
        },
      },

      align: {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-align") || "center",
        renderHTML: (attributes) => ({ "data-align": attributes.align || "center" }),
      },
    };
  },

  // ✅ Persist styling into the HTML itself so viewer respects it too
  renderHTML({ HTMLAttributes }) {
    const width = HTMLAttributes["data-width"] || 100;
    const align = HTMLAttributes["data-align"] || "center";

    return [
      "img",
      {
        ...HTMLAttributes,
        style: `${HTMLAttributes.style || ""};${toStyleString(width, align)}`,
      },
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});

export default ResizableImage;